import { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Box from '@mui/material/Box';
import Container from '@mui/material/Container';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import Accordion from '@mui/material/Accordion';
import AccordionSummary from '@mui/material/AccordionSummary';
import AccordionDetails from '@mui/material/AccordionDetails';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import StatusBadge from '../components/common/StatusBadge';
import EmptyState from '../components/common/EmptyState';
import { TestDetailShimmer } from '../components/shimmer';
import { useGetItemByIdQuery } from '../store/edosApi';
import { useListOffersQuery } from '../store/siteContentApi';
import { mapApiToTestDetail } from '../utils/mapApiDetail';
import { resolvePricing } from '../utils/offerLookup';
import { useCart } from '../context/CartContext';
import { useUI } from '../context/UIContext';
import { ROUTES } from '../constants/routes';

const KeyValueRow = ({ k, v }) => (
  <Box
    sx={{
      display: 'grid',
      gridTemplateColumns: { xs: '115px 1fr', md: '145px 1fr' },
      gap: { xs: 1, md: 1.5 },
      p: { xs: '10px 14px', md: '10px 18px' },
      borderBottom: '1px solid var(--border-l)',
      '&:last-of-type': { borderBottom: 'none' },
    }}
  >
    <Typography sx={{ fontSize: { xs: 12, md: 13 }, fontWeight: 600, color: 'var(--charcoal)' }}>{k}</Typography>
    <Typography sx={{ fontSize: { xs: 12, md: 13 }, color: 'var(--ink)', wordBreak: 'break-word' }}>{v}</Typography>
  </Box>
);

const SectionHeader = ({ label }) => (
  <Box sx={{ bgcolor: 'var(--forest)', p: '10px 18px' }}>
    <Typography
      sx={{ fontSize: 10, fontWeight: 700, color: 'rgba(255,255,255,0.9)', letterSpacing: '0.18em', textTransform: 'uppercase' }}
    >
      {label}
    </Typography>
  </Box>
);

const TestDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { toggleItem, inCart } = useCart();
  const { showToast } = useUI();
  const [bookLoading, setBookLoading] = useState(false);

  const { data, isLoading, isError, error } = useGetItemByIdQuery(id, { skip: !id });
  const rawTest = mapApiToTestDetail(data);

  // Overlay any admin-curated offer for this test. If one is live, we swap
  // `price` for the discounted value and keep `original` as the EDOS mrp.
  // When no offer matches, `rawTest` renders as-is with no "Save ₹0" stub.
  const { data: offers = [] } = useListOffersQuery();
  const pricing = rawTest ? resolvePricing(offers, rawTest.id, rawTest.price) : null;
  const test = rawTest && pricing
    ? { ...rawTest, price: pricing.finalPrice, original: pricing.originalPrice }
    : rawTest;
  const offerBadge = pricing?.badge || '';

  if (isLoading) {
    return <TestDetailShimmer />;
  }

  if (isError || !test || data?.notFound) {
    return (
      <Container sx={{ py: 10 }}>
        <EmptyState
          icon="🔬"
          title="Test not found"
          description={error?.data?.message || "The test you're looking for does not exist or has been removed."}
          action={
            <Button variant="contained" color="primary" onClick={() => navigate(ROUTES.TESTS)}>
              Browse all tests
            </Button>
          }
        />
      </Container>
    );
  }

  const isInCart = inCart(test.id, 'test');

  const handleCart = () => {
    toggleItem({
      id: test.id,
      type: 'test',
      name: test.name,
      price: test.price,
      original: test.original,
      icon: test.icon,
      tat: test.tat,
      paramCount: test.paramCount,
    });
    showToast(isInCart ? 'Removed from cart' : 'Added to cart');
  };

  const handleBookNow = () => {
    if (!isInCart) handleCart();
    setBookLoading(true);
    setTimeout(() => navigate(ROUTES.CART), 200);
  };

  // Breadcrumb back: prefer browser history so a user who landed here
  // from Home/Specials/search returns there, not to the tests list.
  const handleBreadcrumbBack = () => {
    if (window.history.length > 1) navigate(-1);
    else navigate(ROUTES.TESTS);
  };

  const schedDays = (test.schedule || 'M,Tu,W,Th,F,S').split(',');

  return (
    <Box sx={{ bgcolor: 'var(--cream)', minHeight: '100vh' }}>
      <Box sx={{ bgcolor: '#fff', borderBottom: '1px solid var(--border-l)', py: 1.75 }}>
        <Container maxWidth="xl" sx={{ maxWidth: 1300, px: { xs: 2, md: 4 } }}>
          <Box
            sx={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 1,
              lineHeight: 1,
            }}
          >
            <Typography
              component="button"
              onClick={handleBreadcrumbBack}
              sx={{
                fontSize: 13,
                fontWeight: 700,
                color: 'var(--stone)',
                background: 'none',
                border: 0,
                p: 0,
                cursor: 'pointer',
                textDecoration: 'none',
                lineHeight: 1,
                fontFamily: 'inherit',
                '&:hover': { color: 'var(--sage)' },
              }}
            >
              ← Back
            </Typography>
            <Typography
              component="span"
              sx={{ fontSize: 13, color: 'var(--border)', lineHeight: 1 }}
            >
              /
            </Typography>
            <Typography
              component="span"
              sx={{ fontSize: 13, fontWeight: 600, color: 'var(--sage)', lineHeight: 1 }}
            >
              {test.name}
            </Typography>
          </Box>
        </Container>
      </Box>

      <Container maxWidth="xl" sx={{ maxWidth: 1300, py: 4.5, px: { xs: 2, md: 4 } }}>
        {/* Hero */}
        <Box
          sx={{
            bgcolor: 'var(--forest)',
            color: '#fff',
            borderRadius: '20px',
            p: { xs: 3, md: 4 },
            mb: 2.5,
            position: 'relative',
            overflow: 'hidden',
          }}
        >
          <Box
            sx={{
              display: 'flex',
              flexDirection: { xs: 'column', md: 'row' },
              alignItems: { xs: 'flex-start', md: 'center' },
              gap: { xs: 2, md: 3 },
            }}
          >
            {/* Icon */}
            <Box
              sx={{
                width: 64,
                height: 64,
                bgcolor: 'rgba(255,255,255,0.08)',
                borderRadius: '14px',
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: 30,
                border: '1px solid rgba(255,255,255,0.12)',
                flexShrink: 0,
                lineHeight: 1,
              }}
            >
              {test.icon}
            </Box>

            {/* Title block */}
            <Box sx={{ flex: 1, minWidth: 0 }}>
              {test.tag && (
                <StatusBadge tone="amber" sx={{ mb: 1.25 }}>
                  {test.tag}
                </StatusBadge>
              )}
              <Typography
                className="serif"
                sx={{
                  fontSize: { xs: 26, sm: 30, md: 36 },
                  fontWeight: 600,
                  fontStyle: 'italic',
                  color: '#fff',
                  lineHeight: 1.1,
                  mb: 1,
                }}
              >
                {test.name}
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'center' }}>
                <Typography component="span" sx={{ fontSize: 12, color: 'rgba(255,255,255,0.5)' }}>
                  {test.category}
                </Typography>
                <Typography component="span" sx={{ color: 'rgba(255,255,255,0.2)' }}>·</Typography>
                <Typography className="mono" component="span" sx={{ fontSize: 12, color: 'rgba(255,255,255,0.4)' }}>
                  {test.code}
                </Typography>
                <Typography component="span" sx={{ color: 'rgba(255,255,255,0.2)' }}>·</Typography>
                <Typography component="span" sx={{ fontSize: 12, color: 'rgba(255,255,255,0.5)' }}>
                  {test.paramCount} parameter{test.paramCount > 1 ? 's' : ''}
                </Typography>
              </Box>
            </Box>

            {/* Price + buttons */}
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 2,
                flexShrink: 0,
                width: { xs: '100%', md: 'auto' },
              }}
            >
              <Box sx={{ textAlign: { xs: 'left', md: 'right' }, flexShrink: 0 }}>
                <Typography
                  className="serif"
                  sx={{ fontSize: { xs: 28, md: 32 }, fontWeight: 700, color: '#fff', lineHeight: 1 }}
                >
                  ₹{test.price}
                </Typography>
                {/* Original + savings line only when an active offer applies. */}
                {pricing && test.original > test.price && (
                  <>
                    <Typography sx={{ fontSize: 11, textDecoration: 'line-through', color: 'rgba(255,255,255,0.35)', mt: 0.25 }}>
                      ₹{test.original}
                    </Typography>
                    <Stack direction="row" spacing={0.5} alignItems="center" justifyContent={{ xs: 'flex-start', md: 'flex-end' }} sx={{ mt: 0.25 }}>
                      {offerBadge && (
                        <Typography sx={{
                          fontSize: 9, fontWeight: 800, letterSpacing: '0.04em',
                          bgcolor: 'var(--amber)', color: '#fff',
                          px: 0.875, py: 0.125, borderRadius: 100,
                        }}>
                          {offerBadge}
                        </Typography>
                      )}
                      <Typography sx={{ fontSize: 10, fontWeight: 700, color: 'var(--amber)' }}>
                        Save ₹{test.original - test.price}
                      </Typography>
                    </Stack>
                  </>
                )}
              </Box>

              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, flex: { xs: 1, md: 'initial' } }}>
                <Button
                  onClick={handleBookNow}
                  disabled={bookLoading}
                  sx={{
                    bgcolor: 'var(--amber)',
                    color: '#fff',
                    borderRadius: 100,
                    fontSize: 13,
                    fontWeight: 700,
                    textTransform: 'none',
                    px: 2.5,
                    py: 1,
                    whiteSpace: 'nowrap',
                    '&:hover': { bgcolor: '#C77C1D' },
                  }}
                >
                  Book Now
                </Button>
                <Button
                  onClick={handleCart}
                  sx={{
                    bgcolor: 'transparent',
                    color: '#fff',
                    border: '1.5px solid',
                    borderColor: isInCart ? 'var(--sage)' : 'rgba(255,255,255,0.25)',
                    borderRadius: 100,
                    fontSize: 12,
                    fontWeight: 700,
                    textTransform: 'none',
                    px: 2,
                    py: 0.875,
                    whiteSpace: 'nowrap',
                    '&:hover': { borderColor: '#fff', bgcolor: 'rgba(255,255,255,0.06)' },
                  }}
                >
                  {isInCart ? '✓ In Cart' : '+ Add to Cart'}
                </Button>
              </Box>
            </Box>
          </Box>
        </Box>

        {/* Specs + Applicability */}
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
            gap: 2,
            mb: 2,
            width: '100%',
          }}
        >
          <Box sx={{ bgcolor: '#fff', border: '1px solid var(--border-l)', borderRadius: '16px', overflow: 'hidden' }}>
            <SectionHeader label="Test Specifications" />
            <KeyValueRow k="Test Name" v={test.name} />
            <KeyValueRow k="Test Code" v={test.code} />
            <KeyValueRow k="Methodology" v={test.method} />
            <KeyValueRow k="Department" v={test.department} />
            <KeyValueRow k="Disease" v={test.disease} />
            <KeyValueRow k="Doctor Speciality" v={test.specialty} />
          </Box>
          <Box sx={{ bgcolor: '#fff', border: '1px solid var(--border-l)', borderRadius: '16px', overflow: 'hidden' }}>
            <SectionHeader label="Test Applicability" />
            <KeyValueRow k="Gender" v={test.gender} />
            <KeyValueRow k="Age Range" v={test.age} />
            <KeyValueRow k="Home Collection" v={test.homeCol ? '✓ Yes' : '✗ No'} />
            <KeyValueRow k="Walk-in" v={test.walkIn ? '✓ Yes' : '✗ No'} />
            <KeyValueRow k="Fasting Required" v={test.fasting ? 'Yes — 8–12 hrs' : 'Not required'} />
            <KeyValueRow k="Report TAT" v={test.tat} />
          </Box>
        </Box>

        {/* Sample + Schedule */}
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
            gap: 2,
            mb: 2,
            width: '100%',
          }}
        >
          <Box sx={{ bgcolor: '#fff', border: '1px solid var(--border-l)', borderRadius: '16px', overflow: 'hidden' }}>
            <SectionHeader label="Sample Specifications" />
            <KeyValueRow k="Sample Type" v={test.sampleType} />
            <KeyValueRow k="Sample Volume" v={test.sampleVol} />
            <KeyValueRow k="Container" v={test.container} />
            <KeyValueRow k="Ship Temperature" v="2–8°C" />
          </Box>
          <Box sx={{ bgcolor: '#fff', border: '1px solid var(--border-l)', borderRadius: '16px', overflow: 'hidden' }}>
            <SectionHeader label="Test Schedule" />
            <Box sx={{ p: '18px 18px 14px' }}>
              <Typography sx={{ fontSize: 12, fontWeight: 600, color: 'var(--charcoal)', mb: 1.5 }}>
                Schedule Days
              </Typography>
              <Stack direction="row" spacing={0.75} sx={{ mb: 2, flexWrap: 'wrap', gap: 0.5 }}>
                {schedDays.map((d) => (
                  <Box
                    key={d}
                    sx={{
                      width: 34,
                      height: 34,
                      borderRadius: '50%',
                      bgcolor: 'var(--sage)',
                      color: '#fff',
                      fontSize: 11,
                      fontWeight: 700,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    {d}
                  </Box>
                ))}
              </Stack>
              <KeyValueRow k="Sample Receipt Cutoff" v={test.cutoff} />
              <KeyValueRow k="Home Collection" v="✓ Available 6AM–10PM" />
              <KeyValueRow k="Result Mode" v="WhatsApp + App + Email" />
            </Box>
          </Box>
        </Box>

        {/* Additional info */}
        <Box sx={{ bgcolor: '#fff', border: '1px solid var(--border-l)', borderRadius: '16px', overflow: 'hidden', mb: 2 }}>
          <SectionHeader label="Additional Information" />
          <Box sx={{ p: 2.75 }}>
            <Stack direction="row" spacing={1.25} alignItems="center" sx={{ mb: 1.75 }}>
              <Box
                sx={{
                  width: 26,
                  height: 26,
                  borderRadius: '50%',
                  bgcolor: 'var(--sage)',
                  color: '#fff',
                  fontSize: 12,
                  fontWeight: 800,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                {(test.parameters || []).length}
              </Box>
              <Typography sx={{ fontSize: 15, fontWeight: 700, color: 'var(--ink)' }}>Parameters</Typography>
            </Stack>
            <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', gap: 1, mb: 3 }}>
              {(test.parameters || []).map((p) => (
                <StatusBadge key={p} tone="sage">
                  {p}
                </StatusBadge>
              ))}
            </Stack>

            {test.significance && (
              <>
                <Typography sx={{ fontSize: 14, fontWeight: 700, color: 'var(--ink)', mb: 1 }}>
                  Clinical Significance
                </Typography>
                <Typography
                  sx={{ fontSize: 13, color: 'var(--charcoal)', lineHeight: 1.75, mb: 2.5, fontWeight: 300 }}
                >
                  {test.significance}
                </Typography>
              </>
            )}
            {test.instructions && (
              <>
                <Typography sx={{ fontSize: 14, fontWeight: 700, color: 'var(--ink)', mb: 1 }}>
                  Special Instructions
                </Typography>
                <Typography sx={{ fontSize: 13, color: 'var(--charcoal)', lineHeight: 1.75, fontWeight: 300 }}>
                  {test.instructions}
                </Typography>
              </>
            )}
          </Box>
        </Box>

        {/* FAQs */}
        {test.faqs?.length > 0 && (
          <Box sx={{ bgcolor: '#fff', border: '1px solid var(--border-l)', borderRadius: '16px', overflow: 'hidden', mb: 2 }}>
            <SectionHeader label="Frequently Asked Questions" />
            {test.faqs.map(([q, a], i) => (
              <Accordion
                key={i}
                elevation={0}
                sx={{
                  '&:before': { display: 'none' },
                  borderBottom: '1px solid var(--border-l)',
                  '&.Mui-expanded': { margin: 0 },
                }}
              >
                <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ px: 2.5 }}>
                  <Typography sx={{ fontSize: 14, fontWeight: 600, color: 'var(--ink)' }}>{q}</Typography>
                </AccordionSummary>
                <AccordionDetails sx={{ px: 2.5, pb: 2 }}>
                  <Typography sx={{ fontSize: 13, color: 'var(--charcoal)', lineHeight: 1.75, fontWeight: 300 }}>
                    {a}
                  </Typography>
                </AccordionDetails>
              </Accordion>
            ))}
          </Box>
        )}

        {/* Bottom CTA */}
        <Box
          sx={{
            bgcolor: 'var(--forest)',
            color: '#fff',
            borderRadius: '16px',
            p: { xs: 2.5, md: 3 },
            display: 'flex',
            flexDirection: { xs: 'column', sm: 'row' },
            alignItems: { xs: 'stretch', sm: 'center' },
            justifyContent: 'space-between',
            flexWrap: 'wrap',
            gap: 2,
          }}
        >
          <Box>
            <Typography className="serif" sx={{ fontSize: 20, fontWeight: 600, color: '#fff' }}>
              Ready to book?
            </Typography>
            <Typography sx={{ fontSize: 12, color: 'rgba(255,255,255,0.45)' }}>
              Home collection · Trustian in 60 min · Report in {test.tat}
            </Typography>
          </Box>
          <Stack
            direction="row"
            spacing={2}
            alignItems="center"
            justifyContent={{ xs: 'space-between', sm: 'flex-end' }}
          >
            <Box sx={{ textAlign: { xs: 'left', sm: 'right' } }}>
              <Typography className="serif" sx={{ fontSize: 26, fontWeight: 600, color: '#fff', lineHeight: 1 }}>
                ₹{test.price}
              </Typography>
              {pricing && test.original > test.price && (
                <Typography sx={{ fontSize: 11, textDecoration: 'line-through', color: 'rgba(255,255,255,0.35)' }}>
                  ₹{test.original}
                </Typography>
              )}
            </Box>
            <Button variant="contained" color="secondary" size="large" onClick={handleBookNow}>
              Book Now →
            </Button>
          </Stack>
        </Box>
      </Container>
    </Box>
  );
};

export default TestDetail;
